import { isEqual } from "lodash";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { CaseDiagnosis, CptCode, CptInput } from "custom-models";

import { setDictationKey } from "shared/actions/sysActions";
import { useDictationEffect } from "shared/hooks/useDictation";

import ModifyDiagnosisView from "./ModifyDiagnosisView";
import { createApiCptList, reduceCptCodes } from "../DiagnosisDialogHelpers";

type CopiedDiagnosisDialogProps = {
  hat: string;
  completeDiagnosis: CaseDiagnosis;
  copiedDiagnosis: CaseDiagnosis;
  onUpdateDiagnosis: Function;
  onResetDiagnosis: Function;
  isCorrection?: boolean;
  hasScreenDisclaimer?: boolean;
};

const checkDiagDifference = (
  completeDiagnosis: CaseDiagnosis,
  copiedDiagnosis: CaseDiagnosis,
  currCptCodes: CptInput[]
) => {
  const compare = !![
    "diagnosis",
    "comment",
    "microscopic",
    "zsd_code",
    "icd10_code",
  ].find(
    (key) =>
      !isEqual(
        completeDiagnosis[key as keyof CaseDiagnosis],
        copiedDiagnosis[key as keyof CaseDiagnosis]
      )
  );

  return compare
    ? compare
    : !isEqual(completeDiagnosis.cpt_codes, createApiCptList(currCptCodes));
};

const CopiedDiagnosisDialog = ({
  hat,
  completeDiagnosis,
  copiedDiagnosis,
  onUpdateDiagnosis,
  onResetDiagnosis,
  isCorrection,
  hasScreenDisclaimer,
}: CopiedDiagnosisDialogProps) => {
  const dispatch = useDispatch();
  const [cpt, setCpt] = useState<CptInput[]>(
    reduceCptCodes(copiedDiagnosis.cpt_codes || [])
  );

  useDictationEffect(["note", "diagnosis", "microscopic"], (dictation) => {
    if (completeDiagnosis && dictation.text && dictation.key) {
      const diagnosisKey =
        dictation.key === "note"
          ? "comment"
          : (dictation.key as keyof CaseDiagnosis);

      onUpdateDiagnosis(
        diagnosisKey,
        copiedDiagnosis[diagnosisKey]
          ? copiedDiagnosis[diagnosisKey] + " " + dictation.text
          : dictation.text
      );

      dispatch(setDictationKey(null));
    }
  });

  return (
    <ModifyDiagnosisView
      hat={hat}
      caseId={copiedDiagnosis.case}
      addonDiagnosis={copiedDiagnosis}
      completeDiagnosis={completeDiagnosis}
      onUpdateDiagnosis={onUpdateDiagnosis}
      onUpdateDiagnosisCpt={(codes: CptCode[]) => {
        onUpdateDiagnosis("cpt_codes", codes);
      }}
      cptCodes={cpt}
      setCptCodes={setCpt}
      checkDiagnosisDifference={checkDiagDifference}
      onResetDiagnosis={onResetDiagnosis}
      hasScreenDisclaimer={hasScreenDisclaimer}
      isCorrection={isCorrection}
    />
  );
};

export default CopiedDiagnosisDialog;
